"use server";

import { redirect } from "next/navigation";
import { headers } from "next/headers";
import { rawPrisma } from "../lib/prisma";
import { getAppUrl } from "../lib/app-url";
import { verifyPassword } from "../lib/auth/password";
import { getSession } from "../lib/auth/session";
import {
  autoUnlockIfExpired,
  isLockActive,
  registerFailedLogin,
  resetFailedLogins,
  unlockByToken,
} from "../lib/auth/lockout";
import { AuditAction } from "../generated/prisma/client";
import { sendMail } from "../lib/mail/mailer";
import { unlockAccountEmailHtml } from "../lib/mail/templates/unlock-account";

export type LoginState = { error?: string } | undefined;

async function requestMeta() {
  const h = await headers();
  return {
    ipAddress: h.get("x-forwarded-for") ?? undefined,
    userAgent: h.get("user-agent") ?? undefined,
  };
}

export async function login(_prevState: LoginState, formData: FormData): Promise<LoginState> {
  const username = String(formData.get("username") ?? "").trim();
  const password = String(formData.get("password") ?? "");
  const meta = await requestMeta();

  if (!username || !password) {
    return { error: "الرجاء إدخال اسم المستخدم وكلمة المرور" };
  }

  const person = await rawPrisma.person.findFirst({
    where: { username, deletedAt: null, isActive: true },
  });

  if (!person) {
    return { error: "بيانات الدخول غير صحيحة" };
  }

  await autoUnlockIfExpired(person);
  const fresh = await rawPrisma.person.findUniqueOrThrow({ where: { id: person.id } });

  if (fresh.isLocked && isLockActive(fresh)) {
    return { error: "تم قفل الحساب مؤقتاً بسبب محاولات دخول فاشلة متكررة. حاول لاحقاً أو تواصل مع الإدارة." };
  }

  const valid = await verifyPassword(password, fresh.passwordHash);
  if (!valid) {
    const { locked, unlockToken } = await registerFailedLogin(fresh.id, fresh.failedLoginCount, Boolean(fresh.email), meta);

    if (locked && unlockToken && fresh.email) {
      const appUrl = await getAppUrl();
      try {
        await sendMail({
          to: fresh.email,
          subject: "تم قفل حسابك - نظام إدارة المجمع",
          html: unlockAccountEmailHtml({ name: fresh.name, unlockUrl: `${appUrl}/unlock/${unlockToken}` }),
        });
        await rawPrisma.auditLog.create({
          data: { actorId: fresh.id, action: AuditAction.EMAIL_SENT, tableName: "Person", recordId: fresh.id },
        });
      } catch (err) {
        await rawPrisma.auditLog.create({
          data: {
            actorId: fresh.id,
            action: AuditAction.EMAIL_FAILED,
            tableName: "Person",
            recordId: fresh.id,
            newValue: { error: err instanceof Error ? err.message : String(err) },
          },
        });
      }
    }

    return {
      error: locked
        ? "تم قفل الحساب بعد محاولات دخول فاشلة متكررة. تحقق من بريدك الإلكتروني لرابط فك القفل."
        : "بيانات الدخول غير صحيحة",
    };
  }

  await resetFailedLogins(fresh.id, meta);

  const session = await getSession();
  session.personId = fresh.id;
  session.username = fresh.username;
  session.name = fresh.name;
  session.role = fresh.role;
  await session.save();

  redirect(fresh.role === "ADMIN" ? "/dashboard" : "/portal");
}

export async function unlockAccountByToken(token: string): Promise<{ success: boolean }> {
  const meta = await requestMeta();
  const success = await unlockByToken(token, meta);
  return { success };
}

/**
 * Admin break-glass recovery: works even if email is broken. The token is a
 * long random secret generated once (openssl rand -hex 32) and stored only
 * in ADMIN_BREAK_GLASS_TOKEN / hashed on the admin's Person row at seed time
 * — never linked from any UI, only reachable if the operator has saved the
 * URL themselves (e.g. in a password manager).
 */
export async function recoverAdminByToken(token: string): Promise<{ success: boolean }> {
  const meta = await requestMeta();
  const admins = await rawPrisma.person.findMany({
    where: { role: "ADMIN", deletedAt: null, breakGlassTokenHash: { not: null } },
  });

  for (const admin of admins) {
    if (!admin.breakGlassTokenHash) continue;
    const matches = await verifyPassword(token, admin.breakGlassTokenHash);
    if (!matches) continue;

    await rawPrisma.person.update({
      where: { id: admin.id },
      data: { isLocked: false, failedLoginCount: 0, lockedAt: null, unlockToken: null, unlockTokenExpiry: null },
    });
    await rawPrisma.auditLog.create({
      data: {
        actorId: admin.id,
        actorLabel: "break-glass",
        action: AuditAction.ACCOUNT_UNLOCK,
        tableName: "Person",
        recordId: admin.id,
        ipAddress: meta.ipAddress,
        userAgent: meta.userAgent,
      },
    });

    const session = await getSession();
    session.personId = admin.id;
    session.username = admin.username;
    session.name = admin.name;
    session.role = admin.role;
    await session.save();

    redirect("/dashboard");
  }

  return { success: false };
}

export async function logout() {
  const session = await getSession();
  const meta = await requestMeta();
  if (session.personId) {
    await rawPrisma.auditLog.create({
      data: {
        actorId: session.personId,
        actorLabel: session.username,
        action: AuditAction.LOGOUT,
        tableName: "Person",
        recordId: session.personId,
        ipAddress: meta.ipAddress,
        userAgent: meta.userAgent,
      },
    });
  }
  session.destroy();
  redirect("/login");
}
